// src/hooks/useSessionData.js
import { useRef } from 'react';
import { hexToBytes, decryptEventValue } from '../cryptoHelper.js';
import { SessionTimelineCache } from '../sessionCacheHelper.js';
import {
    fetchSessionHistory,
    fetchInitialState,
    fetchRealtimeCredentials,
} from '../apiHelper.js';
import { createSessionRealtimeClient } from '../realtimeHelper.js';
import { accessHeaders, isV2Access, openLinkKeys, withAccessQuery } from '../sessionAccess.js';

export const useSessionData = (BACKEND_API_URL) => {
    const sessionPswdKeyRef = useRef(null);
    // Clé AES-128 des segments HLS : pswdSession en v1, sous-clé dédiée en v2.
    const sessionMediaKeyRef = useRef(null);
    const realtimeClientRef = useRef(null);
    const realtimeEventKeysRef = useRef(new Set());
    const catchUpTimerRef = useRef(null);
    const stateRef = useRef({ allEvents: [], endTime: 0 });
    const initialStateRef = useRef(null); // Snapshot de l'état initial pour le rembobinage
    const timelineCacheRef = useRef(new SessionTimelineCache()); // Cache des snapshots O(log N)
    const virtualSessionRef = useRef({
        currentLocation: null,
        isCorridorEnabled: false,
        victimRoute: [],
        corridorZone: [],
        corridorHoles: [],
        trailApproachPoints: [],
        routePoints: [],
        responders: {}
    });

    const realtimeEventKey = event => {
        if (!event || event.timestamp === undefined || event.timestamp === null) return null;
        const value = typeof event.value === 'string' ? event.value : JSON.stringify(event.value ?? null);
        return `${event.userId || ''}:${event.timestamp}:${event.eventType || ''}:${event.property || event.propertyName || ''}:${value}`;
    };

    const rememberRealtimeEvent = event => {
        const eventKey = realtimeEventKey(event);
        if (!eventKey) return true;
        if (realtimeEventKeysRef.current.has(eventKey)) return false;
        realtimeEventKeysRef.current.add(eventKey);
        return true;
    };

    const dedupCandidate = (processedEvent, decodedEvent, storedEvent = null) => ({
        ...(decodedEvent || {}),
        userId: decodedEvent?.userId || storedEvent?.userId || '',
        timestamp: Number(decodedEvent?.timestamp) || Number(processedEvent?.timestamp),
        eventType: decodedEvent?.eventType || processedEvent?.eventType,
        property: decodedEvent?.property || decodedEvent?.propertyName || '',
        value: decodedEvent?.value ?? processedEvent?.eventData,
    });

    const prepareStoredEvent = async event => {
        const processedEvent = { ...event };
        const rawValue = event.eventData;
        let decodedEvent = null;

        if (typeof rawValue !== 'string') return { processedEvent, decodedEvent };

        const trimmed = rawValue.trim();
        // Seul un événement qui se déchiffre avec la clé de session est
        // authentique : un JSON en clair pourrait venir de n'importe qui.
        let clearValue = null;
        if (!trimmed.startsWith('{') && trimmed.length > 32) {
            clearValue = await decryptEventValue(trimmed, sessionPswdKeyRef.current);
        }

        if (clearValue) {
            try {
                decodedEvent = JSON.parse(clearValue);
                processedEvent.eventData = clearValue;
                processedEvent.eventType = decodedEvent.eventType || decodedEvent.property || event.eventType;
                processedEvent.timestamp = Number(decodedEvent.timestamp) || Number(event.timestamp);
            } catch (parseError) {
                if (clearValue !== trimmed) {
                    console.error("[FRONT] Événement historique déchiffré invalide");
                }
            }
        }

        return { processedEvent, decodedEvent };
    };

    const fetchSessionDetails = async (sessionId) => {
        const apiUrl = withAccessQuery(`${BACKEND_API_URL}/api/session/${sessionId}/details`);
        try {
            // Ne jamais écrire la capacité du lien de partage dans la console.
            console.log("[FRONT] 🌐 Chargement des détails de session");
            const response = await fetch(apiUrl, { headers: await accessHeaders() });
            // Lien fermé (plus d'une heure après la fin) : page de confidentialité.
            if (response.status === 410) return { error: true, expired: true };
            if (!response.ok) throw new Error(response.statusText);
            const data = await response.json();

            if (isV2Access() && data.session && data.session.status !== 'archived') {
                // v2 : le serveur ne connaît pas la clé ; on ouvre l'enveloppe du lien.
                const keys = await openLinkKeys(BACKEND_API_URL, sessionId);
                if (keys) {
                    sessionPswdKeyRef.current = keys.events;
                    sessionMediaKeyRef.current = keys.mediaHls;
                    console.log("[FRONT] 🔑 Enveloppe du lien ouverte.");
                }
            } else if (data.session?.pswdSession) {
                sessionPswdKeyRef.current = hexToBytes(data.session.pswdSession);
                sessionMediaKeyRef.current = sessionPswdKeyRef.current;
                console.log("[FRONT] 🔑 Clé de déchiffrement initialisée.");
            } else {
                console.warn("[FRONT] ⚠️ Aucune clé pswdSession trouvée dans la réponse !");
            }
            return data;
        } catch (error) {
            console.error("[FRONT] ❌ Erreur fetchSessionDetails:", error);
            return { error: true };
        }
    };

    const loadInitialState = async (sessionId, linkkey) => {
        try {
            const stateResult = await fetchInitialState(BACKEND_API_URL, sessionId, linkkey);
            if (stateResult.success && stateResult.initialState) {
                const decryptedRaw = await decryptEventValue(stateResult.initialState, sessionPswdKeyRef.current);
                if (decryptedRaw) {
                    const parsed = JSON.parse(decryptedRaw);
                    return parsed;
                }
            }
        } catch (err) {
            console.error("[FRONT] ❌ Échec initialisation état:", err);
        }
        return null;
    };

    const loadHistoryAndProcess = async (sessionId, linkkey, shouldCancel = () => false) => {
        let lastConvertSessionChange = null;
        try {
            const historyData = await fetchSessionHistory(BACKEND_API_URL, sessionId, linkkey);
            if (shouldCancel()) return null;
            if (historyData && historyData.success && Array.isArray(historyData.events)) {
                const preparedEvents = [];
                const preparedKeys = [];

                for (const e of historyData.events) {
                    const { processedEvent, decodedEvent } = await prepareStoredEvent(e);
                    if (shouldCancel()) return null;
                    preparedKeys.push(dedupCandidate(processedEvent, decodedEvent, e));
                    preparedEvents.push(processedEvent);
                }
                if (shouldCancel()) return null;
                realtimeEventKeysRef.current.clear();
                timelineCacheRef.current.reset(initialStateRef.current);
                preparedKeys.forEach(rememberRealtimeEvent);
                timelineCacheRef.current.replaceEvents(preparedEvents);
                stateRef.current.allEvents = [...timelineCacheRef.current.events];

                const latest = timelineCacheRef.current.getLatestState().snapshot;
                if (latest.sessionType) {
                    lastConvertSessionChange = {
                        type: 'CONVERTSESSION',
                        sessionType: latest.sessionType,
                        trigger: latest.trigger,
                        audio: latest.audio,
                        video: latest.video,
                        shareType: latest.shareType,
                    };
                }
            }
        } catch (e) {
            console.error("[FRONT] ❌ Erreur historique:", e);
        }
        return lastConvertSessionChange;
    };

    const catchUpHistory = async (
        sessionId,
        linkkey,
        onUpdateCallback,
        onConvertSessionCallback
    ) => {
        try {
            const historyData = await fetchSessionHistory(BACKEND_API_URL, sessionId, linkkey);
            if (!historyData?.success || !Array.isArray(historyData.events)) return;

            let hasNewEvents = false;
            for (const event of historyData.events) {
                const { processedEvent, decodedEvent } = await prepareStoredEvent(event);
                if (!rememberRealtimeEvent(dedupCandidate(processedEvent, decodedEvent, event))) continue;

                const previousLatest = timelineCacheRef.current.getLatestState().snapshot;
                timelineCacheRef.current.addEvent(processedEvent);
                const nextLatest = timelineCacheRef.current.getLatestState().snapshot;
                notifyLatestSessionChange(previousLatest, nextLatest, onConvertSessionCallback);
                hasNewEvents = true;
            }
            if (hasNewEvents) {
                stateRef.current.allEvents = [...timelineCacheRef.current.events];
                if (onUpdateCallback) onUpdateCallback();
            }
        } catch (error) {
            console.warn("[MQTT VIEWER] Rattrapage de l'historique différé");
        }
    };

    const processRealtimePayload = async (
        rawPayload,
        onUpdateCallback,
        onConvertSessionCallback
    ) => {
        if (typeof rawPayload !== 'string' || rawPayload.length === 0) return;

        const originalEvent = {
            eventType: 'ENCRYPTED_EVENT',
            eventData: rawPayload,
            timestamp: Date.now(),
        };
        const { processedEvent, decodedEvent } = await prepareStoredEvent({
            ...originalEvent,
            eventData: rawPayload,
        });
        if (!decodedEvent || !rememberRealtimeEvent(dedupCandidate(processedEvent, decodedEvent))) return;

        const previousLatest = timelineCacheRef.current.getLatestState().snapshot;
        timelineCacheRef.current.addEvent(processedEvent);
        const nextLatest = timelineCacheRef.current.getLatestState().snapshot;
        stateRef.current.allEvents = [...timelineCacheRef.current.events];
        notifyLatestSessionChange(previousLatest, nextLatest, onConvertSessionCallback);

        if (onUpdateCallback) onUpdateCallback();
    };

    const notifyLatestSessionChange = (previous, next, callback) => {
        if (!callback || !next) return;
        const changed = !previous
            || previous.sessionType !== next.sessionType
            || previous.trigger !== next.trigger
            || previous.audio !== next.audio
            || previous.video !== next.video
            || previous.shareType !== next.shareType;
        if (!changed) return;

        callback({
            type: 'CONVERTSESSION',
            sessionType: next.sessionType,
            trigger: next.trigger,
            audio: next.audio,
            video: next.video,
            shareType: next.shareType,
        });
    };

    const startRealtimeSync = async (
        sessionId,
        linkkey,
        onUpdateCallback,
        onConvertSessionCallback,
        shouldCancel = () => false
    ) => {
        stopRealtimeSync();
        const credentials = await fetchRealtimeCredentials(BACKEND_API_URL, sessionId, linkkey);
        if (shouldCancel()) return false;
        realtimeClientRef.current = createSessionRealtimeClient({
            credentials,
            sessionId,
            onPayload: payload => {
                processRealtimePayload(
                    payload,
                    onUpdateCallback,
                    onConvertSessionCallback
                ).catch(() => console.error("[MQTT VIEWER] Traitement temps réel impossible"));
            },
            onStatus: (status) => {
                if (status === 'subscribed') {
                    console.log("[MQTT VIEWER] Temps réel actif");
                    if (catchUpTimerRef.current) clearTimeout(catchUpTimerRef.current);
                    // Le court délai laisse au consommateur backend le temps
                    // de persister un événement publié juste avant le SUBACK.
                    catchUpTimerRef.current = setTimeout(() => {
                        catchUpHistory(
                            sessionId,
                            linkkey,
                            onUpdateCallback,
                            onConvertSessionCallback
                        );
                    }, 750);
                }
                else if (status === 'reconnecting') console.log("[MQTT VIEWER] Reconnexion en cours");
                else if (status === 'offline') console.warn("[MQTT VIEWER] Connexion interrompue");
                else if (status === 'subscription-error') console.error("[MQTT VIEWER] Abonnement refusé");
                else if (status === 'error') console.error("[MQTT VIEWER] Connexion impossible");
            },
        });
        return true;
    };

    const stopRealtimeSync = () => {
        if (catchUpTimerRef.current) {
            clearTimeout(catchUpTimerRef.current);
            catchUpTimerRef.current = null;
        }
        if (realtimeClientRef.current) {
            realtimeClientRef.current.end(true);
            realtimeClientRef.current = null;
        }
    };

    return {
        sessionPswdKeyRef,
        sessionMediaKeyRef,
        stateRef,
        initialStateRef,
        timelineCacheRef,
        virtualSessionRef,
        fetchSessionDetails,
        loadInitialState,
        loadHistoryAndProcess,
        startRealtimeSync,
        stopRealtimeSync
    };
};
